import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useContext,
} from "react";
import { flushSync } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import CollapseHeader from "../../../core/common/collapse-header";
import ManageColumns from "../../../components/filters/ManageColumns";
import {
  ContactService,
  Contact,
  extractChangedFields,
} from "./contact.service";
import { TeamService, Team } from "../leads/team.service";
import ContactForm from "./contactForm";
import { showSuccessAlert, showErrorAlert } from "../../../utils/alertUtils";
import { showConfirmationAlert } from "../../uiInterface/base-ui/alertservice";
import { Modal } from "react-bootstrap";
import male from "../../../assets/male.png";
import TableFilters, {
  FilterState,
  FilterConfig,
  mergeTableFilterChanges,
} from "../../../components/filters/TableFilters";
import SortableHeader from "../../../components/filters/SortableHeader";
import SortByDropdown from "../../../components/filters/SortByDropdown";
import ListFilterChips, {
  getClearedListFilters,
} from "../components/ListFilterChips";
import TablePagination from "../../../components/pagination/TablePagination";
import { AuthContext } from "../../../context/authcontext";
import { getAuthHeaders } from "../../../utils/authUtils";
import { API_BASE_URL } from "../../../environment";
import ExportComponent from "../../../components/ExportComponent";
import { formatCustomDateTime } from "../../../utils/dateUtils";
import { sortRecords } from "../../../utils/sortUtils";
import CompanyContactFilterDropdown from "../components/CompanyContactFilterDropdown";
import EntityDetailsModal, {
  EntityDetailSection,
} from "../../../components/EntityDetailsModal/EntityDetailsModal";
import { openOffcanvas } from "../../../utils/offcanvasUtils";

const formatLocation = (
  city: string,
  state: string,
  country: string
): string => {
  const locationParts = [city, state, country].filter(
    (part) => part && part.trim() !== ""
  );
  return locationParts.length > 0 ? locationParts.join(", ") : "";
};

const refreshIconStyles = `
  .spin {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .ti-spin {
    animation: spin 1s linear infinite;
  }

  .custom-pagination .ant-pagination-item {
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    margin: 0 2px;
  }

  .custom-pagination .ant-pagination-item:hover {
    border-color: #007bff;
  }

  .custom-pagination .ant-pagination-item-active {
    background-color: #007bff;
  }

  .custom-pagination .ant-pagination-item-active a {
    color: white;
  }

  .custom-pagination .ant-pagination-prev,
  .custom-pagination .ant-pagination-next {
    border: none;
    margin: 0 2px;
  }

  .custom-pagination .ant-pagination-prev:hover,
  .custom-pagination .ant-pagination-next:hover {
    color: #007bff;
  }

  .custom-pagination .ant-pagination-total-text {
    color: #6b7280;
    font-size: 14px;
  }

  .contact-avatar {
    width: 32px;
    height: 32px;
    object-fit: cover;
    border-radius: 4px;
    transition: all 0.2s ease;
  }

  .contact-avatar:hover {
    transform: scale(1.05);
  }

  .custom-pagination {
    margin: 16px 0;
  }

  .contact-table {
    min-width: 1000px;
  }

  .contact-table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .table-header-with-divider {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .column-divider {
    position: absolute;
    right: -8px;
    top: 50%;
    transform: translateY(-50%);
    width: 1px;
    height: 20px;
    background-color: #e0e0e0;
    opacity: 0.7;
  }
  
  .ant-table-thead > tr > th {
    position: relative;
  }
  
  .ant-table-thead > tr > th .ant-table-column-sorters {
    display: none !important;
  }
  
  .ant-table-thead > tr > th .ant-table-column-sorter {
    display: none !important;
  }

  .contact-avatar-placeholder:hover {
    background-color: #e3f2fd !important;
    transform: scale(1.05);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  .contact-avatar-placeholder:hover i {
    color: #007bff !important;
  }

  .disabled {
    pointer-events: none !important;
    opacity: 0.5 !important;
    cursor: not-allowed !important;
  }

  .disabled:hover {
    opacity: 0.5 !important;
  }

  .disabled-tooltip {
    position: relative;
  }

  .disabled-tooltip:hover::after {
    content: "This feature is currently disabled";
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 1000;
    pointer-events: none;
  }

  @media (max-width: 767px) {
    .contact-table {
      min-width: 100%;
      width: 100%;
    }

    .contact-table th,
    .contact-table td {
      display: table-cell !important;
      white-space: normal !important;
      padding: 8px;
    }

    .contact-name {
      min-width: 150px;
    }
    
    .contact-email {
      min-width: 150px;
    }
    
    .contact-phone {
      min-width: 120px;
    }
    
    .contact-company {
      min-width: 120px;
    }

    .contact-team {
      min-width: 120px;
    }

    .contact-job-title {
      min-width: 120px;
    }

    .contact-status {
      min-width: 80px;
    }

    .contact-actions {
      min-width: 100px;
    }

    .text-break {
      word-break: break-word;
    }

    .avatar-sm {
      width: 30px;
      height: 30px;
    }

    .badge {
      padding: 4px 8px;
      font-size: 12px;
    }
  }
`;

if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.textContent = refreshIconStyles;
  if (!document.head.querySelector("style[data-refresh-icon]")) {
    styleElement.setAttribute("data-refresh-icon", "true");
    document.head.appendChild(styleElement);
  }
}

interface ContactsListViewProps {
  contacts: any[];
  loading: boolean;
  backgroundLoading?: boolean;
  error: string | null;
  pagination: any;
  fetchContacts: (page?: number) => Promise<void>;
  refreshContacts: () => Promise<void>;
  addContact: (contact: any) => void;
  updateContact: (contact: any) => void;
  removeContact: (contactId: number) => void;
  lastFetched: Date | null;
  isInitialized: boolean;
  currentView: "list" | "grid";
  onViewSwitch: (view: "list" | "grid") => void;
  clearBackgroundLoading?: () => void;
}

const ContactsListView = React.memo(
  ({
    contacts,
    loading,
    backgroundLoading = false,
    error,
    pagination,
    fetchContacts,
    refreshContacts,
    addContact,
    updateContact,
    removeContact,
    currentView,
    onViewSwitch,
    isInitialized,
    clearBackgroundLoading,
  }: ContactsListViewProps) => {
    const navigate = useNavigate();
    const { selectedTenantId, auth } = useContext(AuthContext);

    const [currentPage, setCurrentPage] = useState(1);

    const [selectedContact, setSelectedContact] = useState<Contact | null>(
      null
    );
    const [detailsContact, setDetailsContact] = useState<Contact | null>(
      null
    );
    const [isAddContactOpen, setIsAddContactOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formResetTrigger, setFormResetTrigger] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string>("");

    const [selectedItems, setSelectedItems] = useState<number[]>([]);
    const [selectAll, setSelectAll] = useState(false);

    const [filters, setFilters] = useState<FilterState>({
      searchTerm: "",
      dateRange: [null, null] as [Date | null, Date | null],
      industryFilter: [],
      countryFilter: [],
      statusFilter: null,
      sortBy: "created_datetime",
      sortOrder: "desc",
    });

    const [additionalFilters, setAdditionalFilters] = useState<Record<string, any>>({});

    const [filteredContactsData, setFilteredContactsData] = useState<any[]>([]);
    const [isFilterActive, setIsFilterActive] = useState(false);
    const [isFilterLoading, setIsFilterLoading] = useState(false);
    const [filteredTotalCount, setFilteredTotalCount] = useState(0);

    const [teams, setTeams] = useState<Team[]>([]);
    const [loadingTeams, setLoadingTeams] = useState(false);


    const [isBulkDeleting, setIsBulkDeleting] = useState(false);

    const defaultColumnVisibility = {
      first_name: true,
      primary_email: true,
      primary_phone: true,
      city: true,
      source: true,
      company: true,
      assigned_team_id: false,
      assigned_user_id: false,
      is_active: false,
      job_title: false,
      salutation: false,
      last_name: false,
      state: false,
      country: false,
      zip_code: false,
      address_line1: false,
      personal_email: false,
      mobile_phone: false,
      created_datetime: false,
      updated_datetime: false,
      created_by: false,
      updated_by: false,
    };

    const [columnVisibility, setColumnVisibility] = useState<{
      [key: string]: boolean;
    }>(() => {
      try {
        const saved = localStorage.getItem("contacts_column_visibility");
        if (saved) {
          const parsed = JSON.parse(saved);

          const visibility: { [key: string]: boolean } = {
            ...defaultColumnVisibility,
          };

          Object.keys(parsed).forEach((key) => {
            if (key in defaultColumnVisibility) {
              visibility[key] = parsed[key] === true;
            }
          });

          const visibleCount = Object.values(visibility).filter(Boolean).length;
          if (visibleCount === 0) {
            const defaultKeys = Object.keys(defaultColumnVisibility);
            for (const key of defaultKeys) {
              if (
                defaultColumnVisibility[
                key as keyof typeof defaultColumnVisibility
                ]
              ) {
                visibility[key] = true;
                break;
              }
            }
          }

          return visibility;
        }
      } catch (error) {
        console.error(
          "Error loading column visibility from localStorage:",
          error
        );
      }
      return defaultColumnVisibility;
    });

    const saveColumnVisibilityToStorage = useCallback(
      (visibility: { [key: string]: boolean }) => {
        try {
          localStorage.setItem(
            "contacts_column_visibility",
            JSON.stringify(visibility)
          );
        } catch (error) {
          console.error(
            "Error saving column visibility to localStorage:",
            error
          );
        }
      },
      []
    );

    const clearMessages = () => {
      setDeleteError("");
    };

    const filterConfig: FilterConfig = useMemo(
      () => ({
        showSearch: true,
        searchPlaceholder: "Search contacts...",
        searchField: "contact_full_name",
        showDateFilter: true,
        dateField: "created_datetime",
        showIndustryFilter: false,
        showCountryFilter: true,
        countryField: "country",
        pageName: "contact" as const,
        defaultSort: {
          field: "created_datetime",
          order: "desc" as const,
        },
      }),
      []
    );

    const handleApiFilterChange = useCallback(
      (apiFilters: Record<string, any>) => { },
      []
    );

    const handleApiCall = useCallback(
      async (url: string) => {
        if (!selectedTenantId) {
          return;
        }

        try {
          setIsFilterLoading(true);

          const headers = getAuthHeaders();

          const response = await fetch(url, {
            method: "GET",
            headers: headers,
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const result = await response.json();

          if (result.results && Array.isArray(result.results)) {
            setFilteredContactsData(result.results);
            setFilteredTotalCount(result.count || result.results.length);
            setIsFilterActive(true);
          } else if (result.data && Array.isArray(result.data)) {
            setFilteredContactsData(result.data);
            setFilteredTotalCount(result.count || result.data.length);
            setIsFilterActive(true);
          } else if (Array.isArray(result)) {
            setFilteredContactsData(result);
            setFilteredTotalCount(result.length);
            setIsFilterActive(true);
          } else {
            setFilteredContactsData([]);
            setFilteredTotalCount(0);
            setIsFilterActive(true);
          }
        } catch (error: any) {
          console.error("Filter API error:", error);

          if (
            error.response?.status === 401 ||
            error.response?.status === 403
          ) {
            localStorage.removeItem("authCred");
            localStorage.removeItem("authResponse");
            localStorage.removeItem("authTimestamp");
            localStorage.removeItem("selectedTenantId");

            navigate("/login");
            return;
          }

          setFilteredContactsData([]);
          setFilteredTotalCount(0);
          setIsFilterActive(false);
        } finally {
          setIsFilterLoading(false);
        }
      },
      [selectedTenantId]
    );

    const buildContactsFilterUrl = useCallback(
      (
        page: number,
        nextFilters: FilterState,
        nextAdditional: Record<string, any>
      ) => {
        const params = new URLSearchParams();
        params.set("tenant_id", selectedTenantId || "");
        params.set("page", page.toString());
        params.set("page_size", "12");

        if (nextFilters.searchTerm) {
          params.set("contact_full_name", nextFilters.searchTerm);
        }

        const [startDate, endDate] = nextFilters.dateRange;
        if (startDate && endDate) {
          const start = startDate.toISOString().split("T")[0];
          const end = endDate.toISOString().split("T")[0];
          const dayDifference =
            (new Date(end).getTime() - new Date(start).getTime()) /
            (1000 * 60 * 60 * 24);

          if (start === end || dayDifference <= 1) {
            params.set("created_datetime", end);
          } else {
            params.set("created_datetime_gte", start);
            params.set("created_datetime_lte", end);
          }
        } else if (startDate) {
          params.set("created_datetime_gte", startDate.toISOString().split("T")[0]);
        } else if (endDate) {
          params.set("created_datetime_lte", endDate.toISOString().split("T")[0]);
        }

        nextFilters.industryFilter?.forEach((industry) => {
          params.append("industry_type_display_name", industry);
        });

        nextFilters.countryFilter?.forEach((country) => {
          params.append("country", country);
        });

        Object.entries(nextAdditional).forEach(([key, value]) => {
          if (value === null || value === undefined || value === "") return;
          if (key === "teamFilter") {
            params.set("assigned_team_id", value.toString());
          } else if (key === "userFilter") {
            params.set("assigned_user_id", value.toString());
          } else if (key === "sourceFilter") {
            params.set("source", value.toString());
          } else {
            params.set(key, value.toString());
          }
        });

        if (nextFilters.sort_by) {
          params.set("sort_by", nextFilters.sort_by);
        }

        if (!params.has("is_active")) {
          params.set("is_active", "true");
        }

        return `${API_BASE_URL}/contact/?${params.toString()}`;
      },
      [selectedTenantId]
    );

    const loadContacts = useCallback(
      (
        page: number,
        nextFilters: FilterState,
        nextAdditional: Record<string, any>
      ) => {
        if (!selectedTenantId) return;

        const hasActiveFilters =
          !!nextFilters.searchTerm ||
          !!nextFilters.dateRange[0] ||
          !!nextFilters.dateRange[1] ||
          nextFilters.industryFilter.length > 0 ||
          nextFilters.countryFilter.length > 0 ||
          !!nextFilters.sort_by ||
          Object.values(nextAdditional).some(
            (value) => value !== null && value !== undefined && value !== ""
          );

        if (!hasActiveFilters) {
          setIsFilterActive(false);
          setFilteredContactsData([]);
          setFilteredTotalCount(0);
          fetchContacts(page);
          return;
        }

        handleApiCall(buildContactsFilterUrl(page, nextFilters, nextAdditional));
      },
      [selectedTenantId, handleApiCall, buildContactsFilterUrl, fetchContacts]
    );

    const handleFilterChange = useCallback(
      (newFilters: FilterState) => {
        const mergedFilters = mergeTableFilterChanges(filters, newFilters);
        setFilters(mergedFilters);
        setCurrentPage(1);
        loadContacts(1, mergedFilters, additionalFilters);
      },
      [filters, additionalFilters, loadContacts]
    );

    const handlePageChange = useCallback(
      (page: number) => {
        setCurrentPage(page);
        loadContacts(page, filters, additionalFilters);
      },
      [filters, additionalFilters, loadContacts]
    );

    const handleAdditionalFilterChange = useCallback(
      (dynamicFilters: Record<string, any>) => {
        setAdditionalFilters(dynamicFilters);
        setCurrentPage(1);
        loadContacts(1, filters, dynamicFilters);
      },
      [filters, loadContacts]
    );

    const applyListFilters = useCallback(
      (nextFilters: FilterState, nextAdditional: Record<string, any>) => {
        setFilters(nextFilters);
        setAdditionalFilters(nextAdditional);
        setCurrentPage(1);
        loadContacts(1, nextFilters, nextAdditional);
      },
      [loadContacts]
    );

    const handleRemoveListFilters = useCallback(
      (patch: Record<string, any>) => {
        const filterPatch: Record<string, any> = {};
        const additionalPatch: Record<string, any> = {};
        Object.entries(patch).forEach(([key, value]) => {
          if (key in filters) {
            filterPatch[key] = value;
          } else {
            additionalPatch[key] = value;
          }
        });
        applyListFilters(
          { ...filters, ...filterPatch },
          { ...additionalFilters, ...additionalPatch }
        );
      },
      [filters, additionalFilters, applyListFilters]
    );

    const handleRefresh = useCallback(() => {
      if (isFilterActive) {
        loadContacts(currentPage, filters, additionalFilters);
      } else {
        refreshContacts();
      }
    }, [
      isFilterActive,
      loadContacts,
      currentPage,
      filters,
      additionalFilters,
      refreshContacts,
    ]);

    const handleSortChange = useCallback(
      (newSortBy: string, newOrder: "asc" | "desc") => {
        const newFilters = {
          ...filters,
          sortBy: newSortBy,
          sortOrder: newOrder,
        };
        setFilters(newFilters);
        setCurrentPage(1);
      },
      [filters]
    );

    const { filteredContacts, totalFilteredCount } = useMemo(() => {
      if (isFilterActive) {
        return {
          filteredContacts: filteredContactsData,
          totalFilteredCount: filteredTotalCount,
        };
      }

      if (!Array.isArray(contacts)) {
        return { filteredContacts: [], totalFilteredCount: 0 };
      }

      return {
        filteredContacts: contacts,
        totalFilteredCount: pagination.totalCount,
      };
    }, [
      contacts,
      filteredContactsData,
      isFilterActive,
      pagination.totalCount,
      filteredTotalCount,
    ]);

    const paginatedContacts = useMemo(
      () =>
        sortRecords<any>(filteredContacts, filters.sortBy, filters.sortOrder, {
          assigned_team_id: (record) => {
            const team = teams.find((t) => t.team_id === record.assigned_team_id);
            return team ? team.name || team.team_name : null;
          },
          assigned_user_id: (record) => record.assigned_user?.full_name,
          company: (record) =>
            record.company_name ||
            record.company?.company_name ||
            record.company?.name,
          source: (record) =>
            record.lifecycle_source?.lifecycle_source_display_name ||
            record.source,
        }),
      [filteredContacts, filters.sortBy, filters.sortOrder, teams]
    );

    const teamsMap = useMemo(() => {
      const map = new Map<number, string>();
      teams.forEach((team) => {
        map.set(
          team.team_id,
          team.name || team.team_name || `Team ${team.team_id}`
        );
      });
      return map;
    }, [teams]);

    useEffect(() => {
      clearMessages();
    }, []);

    useEffect(() => {
      const fetchTeams = async () => {
        if (!selectedTenantId || !isInitialized) return;

        try {
          setLoadingTeams(true);
          const teamsData = await TeamService.getTeams(
            parseInt(selectedTenantId, 10)
          );
          setTeams(teamsData);
        } catch (error) {
          console.error("Error fetching teams:", error);
          setTeams([]);
        } finally {
          setLoadingTeams(false);

          if (clearBackgroundLoading) {
            clearBackgroundLoading();
          }
        }
      };

      if (isInitialized) {
        fetchTeams();
      }
    }, [selectedTenantId, isInitialized, clearBackgroundLoading]);

    useEffect(() => {
      if (
        !isFilterActive &&
        pagination.currentPage &&
        pagination.currentPage !== currentPage
      ) {
        setCurrentPage(pagination.currentPage);
      }
    }, [pagination.currentPage, currentPage, isFilterActive]);

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      setSelectAll(checked);
      if (checked) {
        const validIds = filteredContacts
          .filter((item: Contact) => item.contact_id)
          .map((item: Contact) => item.contact_id as number);
        setSelectedItems(validIds);
      } else {
        setSelectedItems([]);
      }
    };

    const handleSelectItem = (contactId: number) => {
      setSelectedItems((prev) => {
        const newSelected = prev.includes(contactId)
          ? prev.filter((id) => id !== contactId)
          : [...prev, contactId];

        const validCurrentData = filteredContacts.filter(
          (item: Contact) => item.contact_id
        );
        setSelectAll(
          newSelected.length === validCurrentData.length &&
          validCurrentData.length > 0
        );
        return newSelected;
      });
    };

    useEffect(() => {
      if (filteredContacts.length > 0) {
        const validCurrentData = filteredContacts.filter(
          (item: Contact) => item.contact_id
        );
        const allCurrentSelected = validCurrentData.every(
          (item: Contact) =>
            item.contact_id && selectedItems.includes(item.contact_id)
        );
        setSelectAll(allCurrentSelected && selectedItems.length > 0);
      } else {
        setSelectAll(false);
      }
    }, [filteredContacts.length, selectedItems.length]);

    useEffect(() => {
      saveColumnVisibilityToStorage(columnVisibility);
    }, [columnVisibility, saveColumnVisibilityToStorage]);

    useEffect(() => {
      const addOffcanvas = document.getElementById("add_contact");

      const handleHidden = () => {
        setIsAddContactOpen(false);
      };

      if (addOffcanvas) {
        addOffcanvas.addEventListener("hidden.bs.offcanvas", handleHidden);
      }

      return () => {
        if (addOffcanvas) {
          addOffcanvas.removeEventListener("hidden.bs.offcanvas", handleHidden);
        }
      };
    }, []);

    const closeOffcanvas = useCallback((offcanvasId: string) => {
      const modal = document.getElementById(offcanvasId);
      if (modal) {
        const closeButton = modal.querySelector(
          '[data-bs-dismiss="offcanvas"]'
        ) as HTMLElement;
        if (closeButton) {
          closeButton.click();
        } else {
          const bsOffcanvas = (window as any).bootstrap?.Offcanvas?.getInstance(
            modal
          );
          if (bsOffcanvas) {
            bsOffcanvas.hide();
          }
        }
      }
    }, []);

    const handleDelete = useCallback(async (contact: Contact) => {
      const result = await showConfirmationAlert(
        "Delete Contact",
        `Are you sure you want to delete "${[contact.first_name, contact.last_name].filter(Boolean).join(" ")}"? This action cannot be undone.`,
        async () => {
          setIsDeleting(true);
          try {
            await ContactService.deleteContact(contact.contact_id);
          } finally {
            setIsDeleting(false);
          }
        }
      );

      if (result.isConfirmed && result.value) {
        removeContact(contact.contact_id);

        if (isFilterActive) {
          setFilteredContactsData((prev) =>
            prev.filter((c) => c.contact_id !== contact.contact_id)
          );
        }

        showSuccessAlert(
          `Contact "${[contact.first_name, contact.last_name].filter(Boolean).join(" ")}" deleted successfully!`
        );
        setSelectedContact(null);
      }
    }, [isFilterActive, removeContact]);


    const handleCreate = useCallback(
      async (contact: Partial<Contact>, skipEmailCheck = false) => {
        setIsSubmitting(true);
        try {
          if (!skipEmailCheck && contact.primary_email?.trim()) {
            const emailExists = await ContactService.checkEmailExists(
              contact.primary_email.trim()
            );

            if (emailExists) {
              closeOffcanvas("add_contact");

              const duplicateEmail = contact.primary_email.trim();

              const result = await showConfirmationAlert(
                "Duplicate Contact Found",
                `A contact with email "${duplicateEmail}" already exists in the same tenant.
Do you still want to create this contact?`,
                undefined,
                { confirmButtonText: 'Yes, Create Contact', cancelButtonText: 'No, Cancel' }
              );

              if (result.isConfirmed) {
                handleCreate(contact, true);
              } else {
                setSelectedContact(null);
                setFormResetTrigger((prev) => prev + 1);
              }

              setIsSubmitting(false);
              return;
            }
          }

          let newContact: Contact;

          if ((contact as any).avatarFile) {
            const formData = new FormData();

            Object.keys(contact).forEach((key) => {
              if (key === "avatarFile") {
                formData.append("avatar_url", (contact as any)[key]);
              } else if (key !== "avatarFile") {
                const value = (contact as any)[key];
                if (value !== null && value !== undefined) {
                  formData.append(key, value.toString());
                }
              }
            });

            newContact = await ContactService.createContactWithAvatar(formData);
          } else {
            newContact = await ContactService.createContact(contact);
          }

          addContact(newContact);

          if (isFilterActive) {
            setFilteredContactsData((prev) => [newContact, ...prev]);
          }

          setSelectedContact(null);
          setFormResetTrigger((prev) => prev + 1);
          closeOffcanvas("add_contact");

          setTimeout(() => {
            showSuccessAlert(
              `Contact "${[newContact.first_name, newContact.last_name].filter(Boolean).join(" ")}" created successfully!`
            );
          }, 500);
        } catch (err) {
          console.error("Failed to create contact:", err);
          showErrorAlert("Failed to create contact");
          throw err;
        } finally {
          setIsSubmitting(false);
        }
      },
      [addContact, closeOffcanvas]
    );





    const handleUpdate = useCallback(
      async (id: number, contact: Partial<Contact>) => {
        setIsSubmitting(true);
        try {
          let updatedContact: Contact;
          const originalContact = contacts.find((c) => c.contact_id === id);

          const updatedById =
            typeof contact.updated_by === "object" &&
              contact.updated_by !== null
              ? contact.updated_by.user_id
              : contact.updated_by ?? null;
          const changedFields = originalContact
            ? extractChangedFields(originalContact, contact, updatedById)
            : contact;

          if ((contact as any).avatarFile) {
            const formData = new FormData();

            Object.entries(changedFields).forEach(([key, value]) => {
              if (key === "avatarFile" && value instanceof File) {
                formData.append("avatar_url", value);
              } else if (key === "assigned_user") {
                if (typeof value === "number") {
                  formData.append("assigned_user_id", value.toString());
                }
              } else if (value !== undefined && value !== null) {
                formData.append(key, value.toString());
              }
            });

            if (originalContact?.tenant_id) {
              formData.append("tenant_id", String(originalContact.tenant_id));
            }
            updatedContact = await ContactService.updateContactWithAvatar(
              id,
              formData,
              originalContact
            );
          } else {
            if (
              "assigned_user" in changedFields &&
              (changedFields.assigned_user === undefined ||
                changedFields.assigned_user === null ||
                typeof changedFields.assigned_user !== "number")
            ) {
              delete changedFields.assigned_user;
            }
            updatedContact = await ContactService.updateContact(
              id,
              changedFields,
              originalContact
            );
          }
          updateContact(updatedContact);

          if (isFilterActive) {
            setFilteredContactsData((prev) =>
              prev.map((contact) =>
                contact.contact_id === id ? updatedContact : contact
              )
            );
          }

          closeOffcanvas("edit_contact");
          setSelectedContact(null);
          setIsEditing(false);

          setTimeout(() => {
            showSuccessAlert(
              `Contact "${[updatedContact.first_name, updatedContact.last_name].filter(Boolean).join(" ")}" updated successfully!`
            );
          }, 500);
        } catch (err) {
          console.error("Failed to update contact:", err);
          showErrorAlert("Failed to update contact");
          throw err;
        } finally {
          setIsSubmitting(false);
        }
      },
      [contacts, updateContact, closeOffcanvas]
    );

    const handleBulkDelete = async () => {
      if (selectedItems.length === 0) return;

      const result = await showConfirmationAlert(
        "Delete Selected Records",
        `Are you sure you want to delete ${selectedItems.length} selected record${selectedItems.length > 1 ? "s" : ""}? This action cannot be undone.`,
        async () => {
          setIsBulkDeleting(true);
          try {
            const deletePromises = selectedItems.map((contactId) =>
              ContactService.deleteContact(contactId)
            );
            await Promise.all(deletePromises);
          } finally {
            setIsBulkDeleting(false);
          }
        }
      );

      if (result.isConfirmed && result.value) {
        selectedItems.forEach((contactId) => removeContact(contactId));

        if (isFilterActive) {
          setFilteredContactsData((prev) =>
            prev.filter(
              (contact) => !selectedItems.includes(contact.contact_id)
            )
          );
        }

        setSelectedItems([]);
        setSelectAll(false);

        await showSuccessAlert(
          `${selectedItems.length} contact${selectedItems.length > 1 ? "s" : ""
          } deleted successfully!`
        );
      }
    };;

    const handleEdit = useCallback((contact: Contact) => {
      flushSync(() => {
        setSelectedContact(contact);
        setIsEditing(true);
      });
    }, []);

    const createContactUrl = (contact: Contact): string => {
      return `/contacts/${contact.contact_id}`;
    };

    const handleFormSubmit = useCallback(
      async (formData: Partial<Contact>) => {
        if (selectedContact) {
          await handleUpdate(selectedContact.contact_id, formData);
        } else {
          await handleCreate(formData);
        }
      },
      [selectedContact, handleUpdate, handleCreate]
    );

    const availableColumns = [
      { key: "first_name", label: "Name" },
      { key: "company", label: "Company" },
      { key: "primary_email", label: "Email" },
      { key: "primary_phone", label: "Phone" },
      { key: "city", label: "Location" },
      { key: "source", label: "Source" },
      { key: "assigned_team_id", label: "Assigned Team" },
      { key: "assigned_user_id", label: "Assigned User" },
      { key: "is_active", label: "Status" },
      { key: "job_title", label: "Job Title" },
      { key: "salutation", label: "Salutation" },
      { key: "last_name", label: "Last Name" },
      { key: "state", label: "State" },
      { key: "country", label: "Country" },
      { key: "zip_code", label: "Zip Code" },
      { key: "address_line1", label: "Address" },
      { key: "personal_email", label: "Personal Email" },
      { key: "mobile_phone", label: "Mobile Phone" },
      { key: "created_datetime", label: "Created Date" },
      { key: "updated_datetime", label: "Updated Date" },
      { key: "created_by", label: "Created By" },
      { key: "updated_by", label: "Updated By" },
    ];

    const [orderedAvailableColumns, setOrderedAvailableColumns] = useState(() => {
      const storageKey = `${window.location.pathname}_column_order`;
      const savedOrder = localStorage.getItem(storageKey);
      if (savedOrder) {
        try {
          const orderKeys = JSON.parse(savedOrder);
          return [...availableColumns].sort((a, b) => {
            const indexA = orderKeys.indexOf(a.key);
            const indexB = orderKeys.indexOf(b.key);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
          });
        } catch (e) {
          return [...availableColumns];
        }
      }
      return [...availableColumns];
    });

    const getSortedAvailableColumns = () => {
      return orderedAvailableColumns;
    };

    const handleColumnVisibilityChange = (
      columnKey: string,
      isVisible: boolean
    ) => {
      const currentVisibleCount =
        Object.values(columnVisibility).filter(Boolean).length;

      if (isVisible && currentVisibleCount >= 6) {
        showErrorAlert(
          "Maximum 6 columns can be enabled at once. Please disable another column first."
        );
        return;
      }

      if (!isVisible && currentVisibleCount <= 1) {
        showErrorAlert("At least one column must remain visible.");
        return;
      }

      const newColumnVisibility = {
        ...columnVisibility,
        [columnKey]: isVisible,
      };

      setColumnVisibility(newColumnVisibility);
      saveColumnVisibilityToStorage(newColumnVisibility);
    };

    const handleResetColumns = () => {
      setColumnVisibility(defaultColumnVisibility);
      saveColumnVisibilityToStorage(defaultColumnVisibility);

      const defaultFilters: FilterState = {
        searchTerm: "",
        dateRange: [null, null] as [Date | null, Date | null],
        industryFilter: [],
        countryFilter: [],
        statusFilter: null,
        sortBy: "created_datetime",
        sortOrder: "desc",
      };
      setFilters(defaultFilters);
    };

    const allAvailableColumns = useMemo(
      () => [
        {
          title: (
            <SortableHeader
              title="Name"
              field="first_name"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "first_name",
          key: "first_name",
          render: (text: string, record: Contact) => (
            <h2 className="d-flex align-items-center">
              <div className="me-2">
                <div
                  className="avatar-sm rounded-circle d-flex align-items-center justify-content-center contact-avatar"
                  style={{
                    width: "32px",
                    height: "32px",
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #e9ecef",
                    transition: "transform 0.2s ease",
                    cursor: "pointer",
                    overflow: "hidden",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = "scale(1.05)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  {record.avatar_url ? (
                    <img
                      src={record.avatar_url}
                      alt={[record.first_name, record.last_name].filter(Boolean).join(" ")}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <img
                      src={male}
                      alt={[record.first_name, record.last_name].filter(Boolean).join(" ")}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </div>
              </div>
              <Link
                to={createContactUrl(record)}
                className="d-flex flex-column"
              >
                {[record.salutation, record.first_name, record.last_name].filter(Boolean).join(" ")}
                <span className="text-default">{record.job_title}</span>
              </Link>
            </h2>
          ),
        },
        {
          title: (
            <SortableHeader
              title="Email"
              field="primary_email"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "primary_email",
          key: "primary_email",
          render: (text: string) => text || "-",
        },
        {
          title: (
            <SortableHeader
              title="Phone"
              field="primary_phone"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "primary_phone",
          key: "primary_phone",
          render: (text: string) => text || "-",
        },
        {
          title: (
            <div className="table-header-with-divider">
              <SortableHeader
                title="Location"
                field="city"
                currentSort={filters.sortBy}
                currentOrder={filters.sortOrder}
                onSort={handleSortChange}
              />
              <div className="column-divider"></div>
            </div>
          ),
          dataIndex: "city",
          key: "city",
          render: (text: string, record: Contact) => (
            <div>
              {formatLocation(record.city, record.state, record.country) || "-"}
            </div>
          ),
        },
        {
          title: (
            <div className="table-header-with-divider">
              <SortableHeader
                title="Source"
                field="source"
                currentSort={filters.sortBy}
                currentOrder={filters.sortOrder}
                onSort={handleSortChange}
              />
              <div className="column-divider"></div>
            </div>
          ),
          dataIndex: "source",
          key: "source",
          render: (text: string, record: any) => record.lifecycle_source?.lifecycle_source_display_name || record.source || "-",
        },
        {
          title: (
            <div className="table-header-with-divider">
              <SortableHeader
                title="Company"
                field="company"
                currentSort={filters.sortBy}
                currentOrder={filters.sortOrder}
                onSort={handleSortChange}
              />
              <div className="column-divider"></div>
            </div>
          ),
          dataIndex: "company",
          key: "company",
          render: (_: any, record: Contact) => {
            const companyName =
              record.company_name ||
              record.company?.company_name ||
              record.company?.name ||
              (record.company_id ? `Company ID: ${record.company_id}` : null);

            return <span>{companyName || "No Company"}</span>;
          },
        },
        {
          title: (
            <div className="table-header-with-divider">
              <SortableHeader
                title="Assigned Team"
                field="assigned_team_id"
                currentSort={filters.sortBy}
                currentOrder={filters.sortOrder}
                onSort={handleSortChange}
              />
              <div className="column-divider"></div>
            </div>
          ),
          dataIndex: "assigned_team_id",
          key: "assigned_team_id",
          render: (_: any, record: Contact) => {
            if (!record.assigned_team_id) {
              return <span className="text-muted">No Assigned Team</span>;
            }

            const teamName = teamsMap.get(record.assigned_team_id);
            if (loadingTeams) {
              return (
                <span className="text-muted">
                  <i
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  ></i>
                  Loading...
                </span>
              );
            }

            return (
              <span>{teamName || `Team ID: ${record.assigned_team_id}`}</span>
            );
          },
        },
        {
          title: (
            <div className="table-header-with-divider">
              <SortableHeader
                title="Assigned User"
                field="assigned_user_id"
                currentSort={filters.sortBy}
                currentOrder={filters.sortOrder}
                onSort={handleSortChange}
              />
              <div className="column-divider"></div>
            </div>
          ),
          dataIndex: "assigned_user_id",
          key: "assigned_user_id",
          render: (_: any, record: Contact) => {
            if (!record.assigned_user) {
              return <span className="text-muted">No Assigned User</span>;
            }

            if (
              typeof record.assigned_user === "object" &&
              record.assigned_user
            ) {
              return record.assigned_user.full_name || "N/A";
            }

            if (typeof record.assigned_user === "number") {
              return `User ID: ${record.assigned_user}`;
            }

            return "N/A";
          },
        },
        {
          title: (
            <div className="table-header-with-divider">
              <SortableHeader
                title="Status"
                field="is_active"
                currentSort={filters.sortBy}
                currentOrder={filters.sortOrder}
                onSort={handleSortChange}
              />
              <div className="column-divider"></div>
            </div>
          ),
          dataIndex: "is_active",
          key: "is_active",
          render: (isActive: boolean) => (
            <div>
              {isActive ? (
                <span className="badge badge-pill badge-status bg-success">
                  Active
                </span>
              ) : (
                <span className="badge badge-pill badge-status bg-danger">
                  Inactive
                </span>
              )}
            </div>
          ),
        },
        {
          title: (
            <SortableHeader
              title="Job Title"
              field="job_title"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "job_title",
          key: "job_title",
          render: (text: string, record: Contact) => record.job_title || "-",
        },
        {
          title: (
            <SortableHeader
              title="Salutation"
              field="salutation"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "salutation",
          key: "salutation",
          render: (text: string, record: Contact) => record.salutation || "-",
        },
        {
          title: (
            <SortableHeader
              title="Last Name"
              field="last_name"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "last_name",
          key: "last_name",
          render: (text: string, record: Contact) => record.last_name || "-",
        },
        {
          title: (
            <SortableHeader
              title="State"
              field="state"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "state",
          key: "state",
          render: (text: string, record: Contact) => record.state || "-",
        },
        {
          title: (
            <SortableHeader
              title="Country"
              field="country"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "country",
          key: "country",
          render: (text: string, record: Contact) => record.country || "-",
        },
        {
          title: (
            <SortableHeader
              title="Zip Code"
              field="zip_code"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "zip_code",
          key: "zip_code",
          render: (text: string, record: Contact) => record.zip_code || "-",
        },
        {
          title: (
            <SortableHeader
              title="Address"
              field="address_line1"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "address_line1",
          key: "address_line1",
          render: (text: string, record: Contact) =>
            record.address_line1 || "-",
        },
        {
          title: (
            <SortableHeader
              title="Personal Email"
              field="personal_email"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "personal_email",
          key: "personal_email",
          render: (text: string, record: Contact) =>
            record.personal_email || "-",
        },
        {
          title: (
            <SortableHeader
              title="Mobile Phone"
              field="mobile_phone"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "mobile_phone",
          key: "mobile_phone",
          render: (text: string, record: Contact) => record.mobile_phone || "-",
        },
        {
          title: (
            <SortableHeader
              title="Created Date"
              field="created_datetime"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "created_datetime",
          key: "created_datetime",
          render: (text: string, record: Contact) => {
            if (!record.created_datetime) return "-";
            return formatCustomDateTime(record.created_datetime);
          },
        },
        {
          title: (
            <SortableHeader
              title="Updated Date"
              field="updated_datetime"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "updated_datetime",
          key: "updated_datetime",
          render: (text: string, record: Contact) => {
            if (!record.updated_datetime) return "-";
            return formatCustomDateTime(record.updated_datetime);
          },
        },
        {
          title: (
            <SortableHeader
              title="Created By"
              field="created_by"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "created_by",
          key: "created_by",
          render: (text: string, record: Contact) => {
            if (typeof record.created_by === "object" && record.created_by) {
              return record.created_by.full_name || "N/A";
            }
            return record.created_by ? `User ID: ${record.created_by}` : "N/A";
          },
        },
        {
          title: (
            <SortableHeader
              title="Updated By"
              field="updated_by"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
          ),
          dataIndex: "updated_by",
          key: "updated_by",
          render: (text: string, record: Contact) => {
            if (typeof record.updated_by === "object" && record.updated_by) {
              return record.updated_by.full_name || "N/A";
            }
            return record.updated_by ? `User ID: ${record.updated_by}` : "N/A";
          },
        },
      ],
      [
        filters.sortBy,
        filters.sortOrder,
        handleSortChange,
        teamsMap,
        loadingTeams,
      ]
    );

    const buildContactSections = useCallback(
      (contact: Contact): EntityDetailSection[] => {
        const teamName = contact.assigned_team_id
          ? teamsMap.get(contact.assigned_team_id) ||
          `Team ID: ${contact.assigned_team_id}`
          : null;
        const assignedUserName =
          typeof contact.assigned_user === "object" && contact.assigned_user
            ? contact.assigned_user.full_name
            : typeof contact.assigned_user === "number"
              ? `User ID: ${contact.assigned_user}`
              : null;
        const companyName =
          (contact as any).company_name ||
          (contact as any).company?.company_name ||
          (contact as any).company?.name ||
          null;

        return [
          {
            title: "Contact Info",
            icon: "ti ti-user",
            fields: [
              { label: "Salutation", value: contact.salutation },
              { label: "First Name", value: contact.first_name },
              { label: "Last Name", value: contact.last_name },
              { label: "Job Title", value: contact.job_title },
              {
                label: "Status",
                value: contact.is_active ? "Active" : "Inactive",
              },
              {
                label: "Source",
                value:
                  (contact as any).lifecycle_source
                    ?.lifecycle_source_display_name || contact.source,
              },
            ],
          },
          {
            title: "Contact Details",
            icon: "ti ti-address-book",
            fields: [
              { label: "Primary Email", value: contact.primary_email },
              { label: "Personal Email", value: contact.personal_email },
              { label: "Primary Phone", value: contact.primary_phone },
              { label: "Mobile Phone", value: contact.mobile_phone },
            ],
          },
          {
            title: "Company & Ownership",
            icon: "ti ti-building",
            fields: [
              { label: "Company", value: companyName },
              { label: "Assigned Team", value: teamName },
              { label: "Assigned User", value: assignedUserName },
            ],
          },
          {
            title: "Location",
            icon: "ti ti-map-pin",
            fields: [
              { label: "Address", value: contact.address_line1 },
              { label: "City", value: contact.city },
              { label: "State", value: contact.state },
              { label: "Country", value: contact.country },
              { label: "Zip Code", value: contact.zip_code },
            ],
          },
          {
            title: "Timestamps",
            icon: "ti ti-clock",
            fields: [
              {
                label: "Created",
                value: contact.created_datetime
                  ? formatCustomDateTime(contact.created_datetime)
                  : null,
              },
              {
                label: "Updated",
                value: contact.updated_datetime
                  ? formatCustomDateTime(contact.updated_datetime)
                  : null,
              },
            ],
          },
        ];
      },
      [teamsMap]
    );

    const columns = useMemo(
      () => [
        {
          title: (
            <div className="form-check form-check-md">
              <input
                className="form-check-input"
                type="checkbox"
                id="select-all"
                checked={!!selectAll}
                onChange={handleSelectAll}
              />
            </div>
          ),
          dataIndex: "checkbox",
          key: "checkbox",
          width: "5%",
          render: (text: any, record: Contact) => (
            <div className="form-check form-check-md">
              <input
                className="form-check-input"
                type="checkbox"
                id={`select-${record.contact_id}`}
                checked={
                  record.contact_id
                    ? selectedItems.includes(record.contact_id)
                    : false
                }
                onChange={() =>
                  record.contact_id && handleSelectItem(record.contact_id)
                }
              />
            </div>
          ),
        },
        ...getSortedAvailableColumns()
          .filter((col) => columnVisibility[col.key])
          .map((col) =>
            allAvailableColumns.find((allCol) => allCol.key === col.key)
          )
          .filter(Boolean),
        {
          title: "Actions",
          dataIndex: "actions",
          key: "actions",
          render: (text: string, record: Contact) => (
            <div className="d-flex align-items-center justify-content-end gap-2">
              <Link
                to="#"
                className="action-icon"
                onClick={(e) => {
                  e.preventDefault();
                  setDetailsContact(record);
                }}
              >
                <i className="ti ti-eye text-blue-light"></i>
              </Link>
              <div className="dropdown table-action">
                <Link
                  to="#"
                  className="action-icon"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  data-bs-boundary="viewport"
                  data-bs-popper-config='{"strategy":"fixed"}'
                >
                  <i className="fa fa-ellipsis-v"></i>
                </Link>
                <div className="dropdown-menu dropdown-menu-end">
                  <Link className="dropdown-item" to={createContactUrl(record)}>
                    <i className="ti ti-info-circle text-blue-light"></i> View Details
                  </Link>
                  <Link
                    className="dropdown-item"
                    to="#"
                    data-bs-toggle="offcanvas"
                    data-bs-target="#edit_contact"
                    onClick={() => handleEdit(record)}
                  >
                    <i className="ti ti-edit text-blue"></i> Edit
                  </Link>
                  <Link
                    className="dropdown-item"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleDelete(record);
                    }}
                  >
                    <i className="ti ti-trash text-danger"></i> Delete
                  </Link>
                </div>
              </div>
            </div>
          ),
        },
      ],
      [
        columnVisibility,
        selectedItems,
        selectAll,
        handleSelectAll,
        handleSelectItem,
        getSortedAvailableColumns,
        allAvailableColumns,
        handleEdit,
        handleDelete,
      ]
    );

    return (
      <>
        <div className="page-wrapper">
          <div className="content">
            <div className="row">
              <div className="col-md-12">
                <div className="page-header">
                  <div className="row align-items-center">
                    <div className="col-8">
                      <h4 className="page-title">
                        Contacts
                        <span className="count-title">
                          {filters.searchTerm !== "" ||
                            filters.dateRange[0] !== null ||
                            filters.dateRange[1] !== null ||
                            filters.countryFilter.length > 0 ||
                            isFilterActive
                            ? totalFilteredCount
                            : pagination.totalCount}
                        </span>
                      </h4>
                    </div>
                    <div className="col-4 text-end">
                      <div className="head-icons">
                        <CollapseHeader />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-header">
                    <div className="row align-items-center">
                      <div className="col">
                        <ListFilterChips
                          entityType="contact"
                          filters={{ ...filters, ...additionalFilters }}
                          onRemove={handleRemoveListFilters}
                          onClearAll={() => applyListFilters(getClearedListFilters(), {})}
                          disabled={loading}
                          className="mb-0"
                        />
                      </div>
                      <div className="col-sm-auto">
                        <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                          {selectedItems.length > 0 && (
                            <button
                              type="button"
                              className="btn btn-danger ms-3"
                              onClick={handleBulkDelete}
                              disabled={isBulkDeleting}
                            >
                              <i className="ti ti-trash me-2" />
                              Delete Selected ({selectedItems.length})
                            </button>
                          )}{" "}
                          &nbsp; &nbsp;
                          <ExportComponent
                            entityType="contact"
                            filters={{ ...filters, ...additionalFilters }}
                          />
                          <Link
                            to="#"
                            className="btn btn-primary"
                            data-bs-toggle="offcanvas"
                            data-bs-target="#add_contact"
                            onClick={() => {
                              setSelectedContact(null);
                              setIsEditing(false);
                              clearMessages();
                              setIsAddContactOpen(true);
                            }}
                          >
                            <i className="ti ti-square-rounded-plus me-2" />
                            Add Contact
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card-body d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                    <TableFilters
                      config={filterConfig}
                      data={contacts}
                      onFilterChange={handleFilterChange}
                      onApiFilterChange={handleApiFilterChange}
                      sortBy={filters.sort_by}
                      searchTerm={filters.searchTerm}
                      dateRange={filters.dateRange}
                      hideClearButton
                      tenantId={selectedTenantId || undefined}
                      className="mt-4"
                    />
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      <SortByDropdown
                        value={filters.sort_by}
                        onChange={(sortBy) =>
                          setFilters((prev) => ({ ...prev, sort_by: sortBy }))
                        }
                      />
                      {/* Filter Dropdown */}
                      <CompanyContactFilterDropdown
                        onFilterChange={handleAdditionalFilterChange}
                        currentFilters={additionalFilters}
                        loading={loading}
                        entityType="contact"
                      />
                      <ManageColumns
                        availableColumns={availableColumns}
                        columnVisibility={columnVisibility}
                        onColumnVisibilityChange={handleColumnVisibilityChange}
                        onResetColumns={handleResetColumns}
                        onReorderColumns={setOrderedAvailableColumns}
                      />

                      <div className="view-icons">
                        <Link
                          to="#"
                          className={currentView === "list" ? "active" : ""}
                          onClick={(e) => {
                            e.preventDefault();
                            onViewSwitch("list");
                          }}
                        >
                          <i className="ti ti-list-tree" />
                        </Link>
                        <Link
                          to="#"
                          className={currentView === "grid" ? "active" : ""}
                          onClick={(e) => {
                            e.preventDefault();
                            onViewSwitch("grid");
                          }}
                        >
                          <i className="ti ti-grid-dots" />
                        </Link>
                      </div>
                    </div>
                  </div>
                  {loading ? (
                    <div className="card-body">
                      <div
                        className="d-flex flex-column align-items-center justify-content-center"
                        style={{ minHeight: "60vh" }}
                      >
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="mt-2">
                          <small className="text-muted">
                            Loading Contacts...
                          </small>
                        </div>
                      </div>
                    </div>
                  ) : error ? (
                    <div className="card-body">
                      <div className="alert alert-danger text-center">
                        <i className="ti ti-alert-circle me-2"></i>
                        {error}
                      </div>
                    </div>
                  ) : (
                    <div className="card-body">
                      <div
                        className="table-responsive contact-table-wrapper"
                        style={{ position: "relative" }}
                      >
                        {isFilterLoading && (
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: "rgba(255, 255, 255, 1)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              zIndex: 10,
                              minHeight: "200px",
                            }}
                          >
                            <div className="d-flex align-items-center">
                              <div
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              >
                                <span className="visually-hidden">Loading</span>
                              </div>
                              <span>Filtering results</span>
                            </div>
                          </div>
                        )}
                        <Table
                          columns={columns}
                          dataSource={paginatedContacts}
                          rowKey={(record: Contact) => record.contact_id}
                          rowSelection={false}
                          loading={loading}
                          pagination={false}
                        />
                        <TablePagination
                          currentPage={
                            isFilterActive
                              ? currentPage
                              : pagination.currentPage || currentPage
                          }
                          pageSize={12}
                          totalItems={
                            isFilterActive
                              ? totalFilteredCount
                              : pagination.totalCount
                          }
                          filteredItems={totalFilteredCount}
                          isFiltered={
                            filters.searchTerm !== "" ||
                            filters.dateRange[0] !== null ||
                            filters.dateRange[1] !== null ||
                            filters.countryFilter.length > 0
                          }
                          onPageChange={handlePageChange}
                          onPageSizeChange={() => { }}
                          entityName="Contacts"
                          loading={loading}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="offcanvas offcanvas-end"
          tabIndex={-1}
          id="add_contact"
          style={{ width: "550px" }}

        >
          <div className="offcanvas-header border-bottom">
            <h5 className="fw-semibold">Add New Contact</h5>
            <button
              type="button"
              className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <div className="offcanvas-body">
            {isAddContactOpen && (
              <ContactForm
                contact={undefined}
                onSubmit={handleFormSubmit}
                onCancel={() => {
                  setSelectedContact(null);
                  setIsEditing(false);
                  closeOffcanvas("add_contact");
                }}
                loading={isSubmitting}
                resetTrigger={formResetTrigger}
                isMainApiCompleted={isInitialized}
                isSubmitting={isSubmitting}
              />
            )}
          </div>
        </div>

        <div
          className="offcanvas offcanvas-end"
          tabIndex={-1}
          id="edit_contact"
          style={{ width: "550px" }}

        >
          <div className="offcanvas-header border-bottom">
            <h5 className="fw-semibold">Edit Contact</h5>
            <button
              type="button"
              className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <div className="offcanvas-body">
            {selectedContact && (
              <ContactForm
                contact={selectedContact}
                onSubmit={handleFormSubmit}
                onCancel={() => {
                  setSelectedContact(null);
                  setIsEditing(false);
                  closeOffcanvas("edit_contact");
                }}
                loading={isSubmitting}
                isMainApiCompleted={isInitialized}
                isSubmitting={isSubmitting}
              />
            )}
          </div>
        </div>

        {detailsContact && (
          <EntityDetailsModal
            isOpen={!!detailsContact}
            onClose={() => setDetailsContact(null)}
            title={[
              detailsContact.salutation,
              detailsContact.first_name,
              detailsContact.last_name,
            ]
              .filter(Boolean)
              .join(" ")}
            subtitle={detailsContact.job_title || undefined}
            eyebrow="Contact"
            avatarUrl={detailsContact.avatar_url || null}
            avatarIcon="ti ti-user-up"
            badge={{
              label: detailsContact.is_active ? "Active" : "Inactive",
              tone: detailsContact.is_active ? "success" : "danger",
            }}
            sections={buildContactSections(detailsContact)}
            onEdit={() => {
              const contact = detailsContact;
              setDetailsContact(null);
              handleEdit(contact);
              openOffcanvas("edit_contact");
            }}
          />
        )}




      </>
    );
  }
);

ContactsListView.displayName = "ContactsListView";

export default ContactsListView;
